import React, { useState, useMemo } from "react"
import { css, Theme } from "@emotion/react"
import {
	FULL_PAGE_SIZE,
	initialCustomerPartQueryParams,
	PartCategory,
	PartChemical,
	useOnlinePartBrands,
	usePartCategories,
	usePartChemicals,
} from "@ncs/ncs-api"
import { arrayWrap, toggleArrayItem } from "@ncs/ts-utils"
import { CheckboxGroupEcomm, TrackingEvent, useUrlState, Icon } from "@ncs/web-legos"
import { PartSearchResultsUrlState } from "../../PartSearchResults"
import { useAnalytics } from "~/hooks/useAnalytics"
import { PriceRangeFilter } from "./PriceRangeFilter"
interface NamedItem extends Record<string, unknown> {
	name: string | null | undefined
	id: number | string
}

function sortItemsByName<T extends { name?: string | null; id: number | string }>(
	items: T[] | undefined
): T[] {
	if (!items || items.length === 0) return []
	return [...items].sort((a, b) => {
		const nameA = a.name ?? ""
		const nameB = b.name ?? ""
		return nameA.localeCompare(nameB)
	})
}

export const PartSearchFiltersEcomm: React.FC = () => {
	const [toggleCategories, setToggleCategories] = useState(false)
	const [toggleChemicals, setToggleChemicals] = useState(false)
	const [toggleBrands, setToggleBrands] = useState(false)
	const [togglePrices, setTogglePrices] = useState(false)
	const { trackEvent } = useAnalytics()

	const [categories] = usePartCategories()
	const [chemicals] = usePartChemicals()
	const { data: brands } = useOnlinePartBrands({
		params: {
			pageSize: FULL_PAGE_SIZE,
			isActive: true,
		},
		manualPagination: true,
	})

	const [partQueryParams, { setUrlState }] = useUrlState<PartSearchResultsUrlState>(
		initialCustomerPartQueryParams,
		{
			removeKeysOnUnmount: false,
		}
	)

	const queryBrands = arrayWrap(partQueryParams.brands).map((c) => String(c))
	const sortedBrands = useMemo(() => sortItemsByName(brands), [brands])

	const queryCategories = arrayWrap(partQueryParams.categories).map((c) => String(c))
	const queryChemicals = arrayWrap(partQueryParams.chemicals).map((c) => String(c))

	const sortedCategories = useMemo(() => sortItemsByName(categories), [categories])
	const sortedChemicals = useMemo(() => sortItemsByName(chemicals), [chemicals])

	const showPartsFilter = useMemo(
		() =>
			queryChemicals.length === 0 ||
			categories?.some((c) => queryCategories?.includes(String(c.id))),
		[categories, queryCategories]
	)

	const showChemicalFilter = useMemo(
		() =>
			queryCategories.length === 0 ||
			chemicals?.some((c) => queryChemicals?.includes(String(c.id))),
		[chemicals, queryChemicals]
	)

	const toggleFilterPartCheckbox = (idToToggle: string, name: string | null) => {
		setUrlState((prev: any) => ({
			...prev,
			categories: toggleArrayItem(
				idToToggle,
				arrayWrap(partQueryParams.categories).map((p) => String(p))
			),
			page: 1,
		}))
		trackEvent(TrackingEvent.FILTERS, { id: idToToggle, name })
	}

	const toggleFilterChemicalCheckbox = (idToToggle: string, name: string | null) => {
		setUrlState((prev: any) => ({
			...prev,
			chemicals: toggleArrayItem(
				idToToggle,
				arrayWrap(partQueryParams.chemicals).map((p) => String(p))
			),
			page: 1,
		}))
		trackEvent(TrackingEvent.FILTERS, { id: idToToggle, name })
	}

	const toggleFilterBrands = (idToToggle: string, name: string | null) => {
		setUrlState((prev: any) => ({
			...prev,
			brands: toggleArrayItem(
				idToToggle,
				arrayWrap(partQueryParams.brands).map((p) => String(p))
			),
			page: 1,
		}))
		trackEvent(TrackingEvent.FILTERS, { id: idToToggle, name })
	}

	return (
		<nav>
			<ul>
				{showPartsFilter && (
					<li css={navElement}>
						<button onClick={() => setToggleCategories(!toggleCategories)}>
							<div css={category}>
								PARTS
								{toggleCategories ?
									<Icon icon="chevron-up" color="text" />
								:	<Icon icon="chevron-down" color="text" />}
							</div>
						</button>
						{toggleCategories && (
							<CheckboxGroupEcomm<PartCategory>
								mt={0.75}
								groupName="categories"
								rows={sortedCategories}
								onChange={(category) =>
									toggleFilterPartCheckbox(String(category.id), category.name)
								}
								valueAccessor={(row) => row.id.toString()}
								labelAccessor="name"
								checkedAccessor={(category) =>
									queryCategories.includes(String(category.id))
								}
							/>
						)}
					</li>
				)}
				{showChemicalFilter && (
					<li css={navElement}>
						<button onClick={() => setToggleChemicals(!toggleChemicals)}>
							<div css={category}>
								CHEMICALS
								{toggleChemicals ?
									<Icon icon="chevron-up" color="text" />
								:	<Icon icon="chevron-down" color="text" />}
							</div>
						</button>
						{toggleChemicals && (
							<CheckboxGroupEcomm<PartChemical>
								mt={0.75}
								groupName="chemicals"
								rows={sortedChemicals}
								onChange={(chemical) =>
									toggleFilterChemicalCheckbox(
										String(chemical.id),
										chemical.name
									)
								}
								valueAccessor={(row) => row.id.toString()}
								labelAccessor="name"
								checkedAccessor={(chemical) =>
									queryChemicals.includes(String(chemical.id))
								}
							/>
						)}
					</li>
				)}
				<li css={navElement}>
					<button onClick={() => setToggleBrands(!toggleBrands)}>
						<div css={category}>
							BRANDS
							{toggleBrands ?
								<Icon icon="chevron-up" color="text" />
							:	<Icon icon="chevron-down" color="text" />}
						</div>
					</button>
					{toggleBrands && (
						<CheckboxGroupEcomm<NamedItem>
							mt={0.75}
							groupName="brands"
							rows={sortedBrands as unknown as NamedItem[]}
							onChange={(brand) =>
								toggleFilterBrands(String(brand.id), brand.name ?? null)
							}
							valueAccessor={(row) => row.id.toString()}
							labelAccessor="name"
							checkedAccessor={(brand) => queryBrands.includes(String(brand.id))}
						/>
					)}
				</li>
				<li css={navElement}>
					<button onClick={() => setTogglePrices(!togglePrices)}>
						<div css={category}>
							PRICE
							{togglePrices ?
								<Icon icon="chevron-up" color="text" />
							:	<Icon icon="chevron-down" color="text" />}
						</div>
					</button>
					{togglePrices && (
						<PriceRangeFilter
							partQueryParams={partQueryParams}
							setUrlState={setUrlState}
							trackEvent={trackEvent}
						/>
					)}
				</li>
			</ul>
		</nav>
	)
}

const navElement = (theme: Theme) => css`
	font-weight: 500;
	padding: 1.5rem 0;
	border-bottom: 1px solid #e5e7eb;

	&:first-of-type {
		margin-top: 1.5rem;
		${theme.breakpoints.down("xs")} {
			margin-top: 0;
		}
	}

	&:last-of-type {
		border-bottom: 0;
	}

	a {
		font-weight: 500;
		color: #00050a;
		text-decoration: none;
	}

	button {
		background: none;
		border: none;
		cursor: pointer;
		padding: 0;
		width: 100%;
	}
`

const category = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: #00050a;
	font-size: 1rem;
`
